// @givmo/compliance — Send for signature (CCM-R1-SIGN-WEB-B; slice 2 §6(a), frame 36, approved as
// shown, D-CCM-22), and the same sheet for "Change the signer or title" (a revision).
//
// The form and the version a send names (recorded by the Download rule before the sheet opens),
// who signs each place the form declares with the title as the organization states it, the
// sign-by date, a note, the factual line and row 7's sentence. Sending is a Compliance Owner's or a
// Global Admin's (D-SIGN-2); the server decides and refuses in its own words, shown as served. Each
// box takes no more than the server does, and a revision's Send waits until a signer, a title or the
// note differs from the live version (CCM-R1-SIGN-WEB-C).
//
// ⚠ NAMED DIFFERENCES FROM FRAME 36. The requirement sentence and the title choices follow the
// rule's served order (B4): the Annual Update's is the statute's, "Chairman, President or other
// principal officer". Nothing is chosen for the person: the frame shows a sheet already filled in.

import { useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  Button,
  ErrorMessage,
  Field,
  Icon,
  Select,
  Sheet,
  Textarea,
  testIdProp,
} from '@givmo/design-system';
import { FileText } from 'lucide-react';
import type { SubmissionChannel } from '@givmo/api-client';
import { useCompliance, type ComplianceSignerCandidate } from './compliance-context';
import { isActConflict, serverMessage } from './compliance-decode';
import { ComplianceDisclosure } from './compliance-disclosure';
import { ComplianceDateField } from './compliance-date-field';
import {
  ATTACHMENTS_GO_LINE,
  CHANGE_THE_SIGNER,
  EXTEND_LATER_LINE,
  INVITE_TO_COMPLIANCE,
  MESSAGE_OPTIONAL,
  NOTE_PLACEHOLDER,
  ONLY_PEOPLE_WITH_ACCESS,
  PREVIEW,
  SEND_FOR_SIGNATURE,
  SIGNER_LABEL,
  SIGN_BY,
  SOMETHING_ELSE,
  TITLE_AS_STATED_LABEL,
  TITLE_RECORDED_LINE,
  WHO_SIGNS,
  browserTimeZone,
  chooseChannel,
  sendChannelOf,
  defaultSignByDay,
  designationsFor,
  nameOf,
  placeLine,
  requirementSentence,
  revisionChanges,
  signByInstant,
  versionLine,
} from './compliance-sending-model';
import { THE_FORM, CANCEL } from './compliance-preparation-model';
import {
  SIGNING_TEXT_LIMITS,
  type SigningPlaces,
  type SigningRequestDetail,
} from './compliance-signing-decode';
import { useOpenVersion } from './signing-shared';
import { QuietButton } from './filing-workbench-pages';

const PREFIX = 'compliance-send-sheet';

/** Built: an act that failed with no words of its own. */
const RECORD_FAILED = 'We couldn’t record that. Please try again.';

/** The choice that opens a title in the organization's own words. */
const OTHER = '__other__';

/** What the sheet sends: one version for the first time, or a revision of an open request. */
export type SendSheetTarget =
  | {
      kind: 'send';
      places: SigningPlaces;
      version: {
        documentId: string;
        versionId: string;
        versionNumber: number;
        createdAt?: string;
        pageCount: number;
      };
    }
  | { kind: 'revise'; places: SigningPlaces; detail: SigningRequestDetail };

interface PlaceChoice {
  signerUserId: string;
  /** A served title, or {@link OTHER} for the organization's own words. */
  role: string;
  other: string;
}

function capacityOf(choice: PlaceChoice | undefined): string {
  if (choice === undefined) return '';
  return (choice.role === OTHER ? choice.other : choice.role).trim();
}

/** The places' starting choices: none for a first send; the live version's for a revision. */
function initialChoices(target: SendSheetTarget): Map<string, PlaceChoice> {
  const choices = new Map<string, PlaceChoice>();
  if (target.kind !== 'revise') return choices;
  const roles = target.places.signerRequirement?.roles ?? [];
  for (const slot of target.detail.slots) {
    const served = roles.includes(slot.capacityText);
    choices.set(slot.slotKey, {
      signerUserId: slot.signerUserId,
      role: served ? slot.capacityText : OTHER,
      other: served ? '' : slot.capacityText,
    });
  }
  return choices;
}

/** The Signer list: the roster's people with Compliance, by name (their email only to tell two apart). */
function signerOptions(
  signers: readonly ComplianceSignerCandidate[],
  keep: readonly { userId: string; name: string }[],
) {
  const all = [...signers];
  for (const kept of keep) {
    if (!all.some((signer) => signer.userId === kept.userId)) all.push(kept);
  }
  const counts = new Map<string, number>();
  for (const signer of all) counts.set(signer.name, (counts.get(signer.name) ?? 0) + 1);
  return all.map((signer) => ({
    value: signer.userId,
    label:
      (counts.get(signer.name) ?? 0) > 1 && signer.email !== undefined
        ? `${signer.name} · ${signer.email}`
        : signer.name,
  }));
}

export function SendForSignatureSheet({
  target,
  formLine,
  jurisdiction,
  onClose,
  onDone,
  onChanged,
}: {
  target: SendSheetTarget | null;
  /** The form and when it is for ("Annual Update of Registration Form · fiscal year ended …"). */
  formLine: string;
  jurisdiction: string;
  onClose: () => void;
  /** The request as the send or revision answered it. */
  onDone: (answer: unknown) => void;
  /**
   * Nothing, to read the case's requests again: a send or a revision refused because the request
   * moved on meanwhile (any 409). The sheet stays open with the served line.
   */
  onChanged: (answer: unknown) => void;
}) {
  return (
    <Sheet.Root
      open={target !== null}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      {target !== null && (
        <Sheet.Content size="lg" testId={PREFIX} initialFocus={false}>
          <SendForm
            target={target}
            formLine={formLine}
            jurisdiction={jurisdiction}
            onClose={onClose}
            onDone={onDone}
            onChanged={onChanged}
          />
        </Sheet.Content>
      )}
    </Sheet.Root>
  );
}

function SendForm({
  target,
  formLine,
  jurisdiction,
  onClose,
  onDone,
  onChanged,
}: {
  target: SendSheetTarget;
  formLine: string;
  jurisdiction: string;
  onClose: () => void;
  onDone: (answer: unknown) => void;
  onChanged: (answer: unknown) => void;
}) {
  const { orgRef, operations, complianceSigners, inviteToCompliance, personName } = useCompliance();
  const openVersion = useOpenVersion();
  const [choices, setChoices] = useState(() => initialChoices(target));
  const [signBy, setSignBy] = useState(() => defaultSignByDay(new Date()));
  const [message, setMessage] = useState(() =>
    target.kind === 'revise' ? (target.detail.version.message ?? '') : '',
  );

  const places = target.places.places;
  const roles = target.places.signerRequirement?.roles ?? [];
  const channel =
    target.kind === 'revise' && target.detail.version.intendedChannel !== undefined
      ? (target.places.channels.find(
          (candidate) => candidate.channel === target.detail.version.intendedChannel,
        ) ?? chooseChannel(target.places.channels))
      : sendChannelOf(target.places);
  const factual =
    channel !== undefined && channel.support.factualLineApplies
      ? channel.support.factualLine
      : undefined;
  const sentence = requirementSentence(jurisdiction, target.places.signerRequirement);
  const keep =
    target.kind === 'revise'
      ? target.detail.slots.map((slot) => ({
          userId: slot.signerUserId,
          name: nameOf(personName, slot.signerUserId),
        }))
      : [];
  const options = useMemo(
    () => signerOptions(complianceSigners ?? [], keep),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the kept names follow the target
    [complianceSigners, target],
  );
  const titleOptions = [
    ...roles.map((role) => ({ value: role, label: role })),
    { value: OTHER, label: SOMETHING_ELSE },
  ];
  const documentId = target.places.documentId;
  const designations = designationsFor(
    documentId,
    places,
    new Map(
      places.map((place) => {
        const choice = choices.get(place.slotKey);
        return [
          place.slotKey,
          { signerUserId: choice?.signerUserId ?? '', capacityText: capacityOf(choice) },
        ] as const;
      }),
    ),
  );
  const complete =
    places.length > 0 &&
    designations.length === places.length &&
    designations.every((one) => one.signer_user_id.length > 0 && one.capacity_text.length > 0);
  const expiresAt = target.kind === 'send' ? signByInstant(signBy, new Date()) : undefined;
  const note = message.trim().length > 0 ? message.trim() : null;
  // "Change the signer or title" sends only once something differs from the live version (N5).
  const changed = target.kind === 'send' || revisionChanges(target.detail, designations, note);
  const ready =
    complete &&
    channel !== undefined &&
    message.trim().length <= SIGNING_TEXT_LIMITS.note &&
    (target.kind === 'revise' || expiresAt !== undefined) &&
    changed;

  const send = useMutation({
    mutationFn: () => {
      if (target.kind === 'send') {
        return operations.issueSigningRequest(orgRef ?? '', {
          filing_case_id: target.places.filingCaseId,
          documents: [
            {
              document_id: target.version.documentId,
              document_version_id: target.version.versionId,
            },
          ],
          designations,
          intended_channel: (channel?.channel ?? '') as SubmissionChannel,
          time_zone: browserTimeZone(),
          expires_at: expiresAt ?? null,
          message: note,
        });
      }
      return operations.reviseSigningRequest(orgRef ?? '', target.detail.request.requestId, {
        designations,
        expected_version_number: target.detail.version.versionNumber,
        intended_channel: (channel?.channel ?? '') as SubmissionChannel,
        time_zone: target.detail.version.timeZone ?? browserTimeZone(),
        message: note,
      });
    },
    onSuccess: (answer) => onDone(answer),
    // The request moved on meanwhile (sent, revised or cancelled elsewhere, or its case withdrawn):
    // the workbench behind reads the case's requests again (CCM-R1-SIGN-WEB-C), and the sheet keeps
    // the served line.
    onError: (error) => {
      if (isActConflict(error)) onChanged(undefined);
    },
  });

  const setChoice = (slotKey: string, change: Partial<PlaceChoice>) =>
    setChoices((current) => {
      const next = new Map(current);
      next.set(slotKey, {
        signerUserId: '',
        role: '',
        other: '',
        ...current.get(slotKey),
        ...change,
      });
      return next;
    });

  const versionId =
    target.kind === 'send' ? target.version.versionId : target.places.documentVersionId;
  return (
    <>
      <Sheet.Header>
        <Sheet.Title className="font-sans text-s18 font-w600 tracking-givmo text-text-strong">
          {target.kind === 'send' ? SEND_FOR_SIGNATURE : CHANGE_THE_SIGNER}
        </Sheet.Title>
      </Sheet.Header>
      <Sheet.Body className="flex flex-col gap-28">
        <section className="flex flex-col gap-10" {...testIdProp(`${PREFIX}-form`)}>
          <h2 className="text-s14 font-w600 text-text-strong">{THE_FORM}</h2>
          <div className="flex items-start gap-12 rounded-lg border border-border-subtle px-16 py-12">
            <Icon icon={FileText} size={18} className="mt-2 shrink-0 text-icon-muted" aria-hidden />
            <div className="flex min-w-0 flex-1 flex-col gap-2">
              <p className="text-s14 text-text">{formLine}</p>
              {target.kind === 'send' && (
                <p className="text-s12 text-text-tertiary" {...testIdProp(`${PREFIX}-version`)}>
                  {versionLine(
                    target.version.createdAt,
                    target.version.versionNumber,
                    target.version.pageCount,
                  )}
                </p>
              )}
            </div>
            <QuietButton
              onClick={() => void openVersion(documentId, versionId)}
              testId={`${PREFIX}-preview`}
            >
              {PREVIEW}
            </QuietButton>
          </div>
          <p className="text-s12 leading-h15 text-text-tertiary">{ATTACHMENTS_GO_LINE}</p>
        </section>

        <section className="flex flex-col gap-10" {...testIdProp(`${PREFIX}-who`)}>
          <h2 className="text-s14 font-w600 text-text-strong">{WHO_SIGNS}</h2>
          {sentence !== undefined && (
            <p
              className="text-s13 leading-h15 text-text-secondary"
              {...testIdProp(`${PREFIX}-requirement`)}
            >
              {sentence}
            </p>
          )}
          {places.map((place) => {
            const choice = choices.get(place.slotKey);
            return (
              <div
                key={place.slotKey}
                className="flex flex-col gap-12 rounded-lg border border-border-subtle p-16"
                {...testIdProp(`${PREFIX}-place-${place.slotKey}`)}
              >
                <p className="text-s13 font-w600 text-text-strong">{placeLine(place.page)}</p>
                <div className="grid grid-cols-1 gap-12 sm:grid-cols-2">
                  <Select
                    appearance="saas"
                    label={SIGNER_LABEL}
                    value={choice?.signerUserId || null}
                    onValueChange={(next) => setChoice(place.slotKey, { signerUserId: next ?? '' })}
                    options={options}
                    disabled={complianceSigners === undefined && target.kind === 'send'}
                    testId={`${PREFIX}-signer-${place.slotKey}`}
                  />
                  <Select
                    appearance="saas"
                    label={TITLE_AS_STATED_LABEL}
                    value={choice?.role || null}
                    onValueChange={(next) => setChoice(place.slotKey, { role: next ?? '' })}
                    options={titleOptions}
                    testId={`${PREFIX}-title-${place.slotKey}`}
                  />
                </div>
                {choice?.role === OTHER && (
                  <Field
                    aria-label={TITLE_AS_STATED_LABEL}
                    value={choice.other}
                    maxLength={SIGNING_TEXT_LIMITS.capacityText}
                    onChange={(event) => setChoice(place.slotKey, { other: event.target.value })}
                    testId={`${PREFIX}-title-other-${place.slotKey}`}
                  />
                )}
                <p className="text-s12 leading-h15 text-text-tertiary">{TITLE_RECORDED_LINE}</p>
              </div>
            );
          })}
          <p className="text-s13 text-text-secondary" {...testIdProp(`${PREFIX}-access`)}>
            {ONLY_PEOPLE_WITH_ACCESS}
            {inviteToCompliance !== undefined && (
              <>
                {' '}
                <a
                  href={inviteToCompliance.href}
                  onClick={(event) => {
                    event.preventDefault();
                    onClose();
                    inviteToCompliance.open();
                  }}
                  className="rounded-xs text-s13 text-text-secondary underline underline-offset-2 hover:text-text-strong"
                  {...testIdProp(`${PREFIX}-invite`)}
                >
                  {INVITE_TO_COMPLIANCE}
                </a>
              </>
            )}
          </p>
        </section>

        {target.kind === 'send' && (
          <section className="flex flex-col gap-10">
            <h2 className="text-s14 font-w600 text-text-strong">{SIGN_BY}</h2>
            <ComplianceDateField
              label={SIGN_BY}
              labelHidden
              long
              helper={EXTEND_LATER_LINE}
              value={signBy}
              onChange={setSignBy}
              testId={`${PREFIX}-sign-by`}
            />
          </section>
        )}

        <Textarea
          label={MESSAGE_OPTIONAL}
          placeholder={NOTE_PLACEHOLDER}
          rows={3}
          maxLength={SIGNING_TEXT_LIMITS.note}
          value={message}
          onChange={(event) => setMessage(event.target.value)}
          testId={`${PREFIX}-message`}
        />

        <div className="flex flex-col gap-8">
          {factual !== undefined && (
            <p className="text-s13 leading-h15 text-text" {...testIdProp(`${PREFIX}-factual`)}>
              {factual}
            </p>
          )}
          <ComplianceDisclosure surface="send-for-signature" />
        </div>
      </Sheet.Body>
      <Sheet.Footer>
        <div className="flex flex-col items-end gap-8">
          {send.error !== null && send.error !== undefined && (
            <ErrorMessage testId={`${PREFIX}-failure`}>
              {serverMessage(send.error, RECORD_FAILED)}
            </ErrorMessage>
          )}
          <div className="flex gap-8">
            <Button
              appearance="saas"
              tone="primary"
              size="md"
              disabled={!ready}
              loading={send.isPending}
              onClick={() => send.mutate()}
              testId={`${PREFIX}-send`}
            >
              {SEND_FOR_SIGNATURE}
            </Button>
            <Button
              appearance="saas"
              tone="ghost"
              size="md"
              onClick={onClose}
              disabled={send.isPending}
              testId={`${PREFIX}-cancel`}
            >
              {CANCEL}
            </Button>
          </div>
        </div>
      </Sheet.Footer>
    </>
  );
}
